import React, { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { useI18n } from "../i18n/I18nProvider";
import { SHOWCASE } from "../constants/testIds";
import { Reveal } from "../components/Reveal";

// Parses a value like "82%", "40,000+", "340%" into number + suffix/prefix
function parseValue(value) {
  const m = String(value).match(/^([^\d]*)([\d,\.]+)(.*)$/);
  if (!m) return { prefix: "", num: 0, suffix: value, hasSeparator: false };
  const prefix = m[1] || "";
  const raw = m[2];
  const suffix = m[3] || "";
  const hasSeparator = raw.includes(",");
  const num = Number(raw.replace(/,/g, ""));
  return { prefix, num, suffix, hasSeparator };
}

const formatNum = (n, hasSeparator) => {
  const rounded = Math.round(n);
  return hasSeparator ? rounded.toLocaleString("en-US") : String(rounded);
};

const Counter = ({ value, duration = 1800 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const { prefix, num, suffix, hasSeparator } = parseValue(value);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(num * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, num, duration]);

  return (
    <span ref={ref} className="tabular-nums">
      {prefix}
      {formatNum(display, hasSeparator)}
      {suffix}
    </span>
  );
};

export const Showcase = () => {
  const { t } = useI18n();
  const stats = t.showcase.stats;

  return (
    <section
      id="showcase"
      data-testid={SHOWCASE.section}
      className="relative bg-black py-32 md:py-44 lg:py-52 px-6 md:px-10 lg:px-14 border-t border-white/5"
    >
      <div className="mx-auto max-w-[1440px]">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 items-end mb-16 md:mb-24">
          <Reveal>
            <div className="flex items-center gap-4 text-[10px] tracking-[0.4em] uppercase font-mono text-white/50">
              <span className="w-8 h-px bg-white/30" />
              {t.showcase.eyebrow}
            </div>
            <h2 className="mt-8 font-display tracking-display text-white text-[34px] sm:text-[44px] md:text-[60px] lg:text-[72px] leading-[1.02] font-medium max-w-[900px]">
              {t.showcase.title}
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-white/55 text-[13px] md:text-[14px] leading-[1.6] max-w-[280px] font-mono tracking-[0.04em]">
              {t.showcase.caption}
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px border border-white/8 bg-white/8">
          {stats.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.08}>
              <div
                data-testid={SHOWCASE.stat(s.slug)}
                className="bg-black p-10 md:p-14 min-h-[260px] md:min-h-[320px] flex flex-col justify-between"
              >
                <span className="font-mono text-[10px] tracking-[0.32em] text-white/40">
                  / {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <div className="font-display text-white text-[64px] md:text-[88px] lg:text-[112px] leading-[0.95] tracking-tight">
                    <Counter value={s.value} />
                  </div>
                  <div className="mt-6 text-white/60 text-[13px] md:text-[14px] leading-[1.55] max-w-[26ch]">
                    {s.label}
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Showcase;
